import us from './defines';
import { Point } from './lib/objects/';

// ________________________________________________________________________________________________
//    This file contains an object that places overlay objects on a viewport.
// ________________________________________________________________________________________________
//    Overview: This object performs layout management for all overlayed visuals in the viewports.
//    Its purpose is to facilitate the placement of or moving of these visuals when new
//    visuals are added or when the viewport is repainted.
//  Upon any change the state vector object for the viewport is informed. //cp-reword me!!!
//
//    Notes:
//        An element's layout array contains:
//                divClass: "us.clsDIVLABEL",    - the class of the object; used to find it in the viewport div
//                                                 there must be only one item with this class per viewport
//                precinct: "BR"                 - general location: "BR" (bottom right), "TL" (top left), "C" (center)
//                                                 "BL", "TR", ...
//                order: 0                       - when more than one item vies for position, what horizontal order to place them;
//                                                 see the note below regarding Flow Order Scheme.
//                bottomOffset: NUM              - a y offset to apply to the position
//                rightOffset: NUM               - an x offset to apply to the position
//                canBePlaced: true              - if false, reposition only if the element is not fully contained in the viewport (e.g. resize event)
//
//        Flow Order Scheme: the order designations indicate a flow order from the corner of
//                            the precinct (e.g. TL) towards the viewport center
//
//        "TL"                                      "TR"
//        ==============================================
//        =  0 1 2                              2 1 0  =
//        =                                            =
//        =                                            =
//        =                 Viewport                   =
//        =                                            =
//        =  0 1 2                              2 1 0  =
//        ==============================================
//        "BL"                                      "BR"
//                             "B"
//
// todo: instead of using kOVERLAY_ELEMENT_SPACE, set overlay element space in css
// todo: rename bottomOffset and rightOffset to x and y, the sign is a little odd too
export default function cOverLayout (viewport) {
  const self = this;
  const $viewportDiv = viewport.vpDivGet('outer');
  const dims = viewport.vpDivDimsGet();

  // Holds refs to the original item so changes can be made to the original item.
  const overlays = [];

  const horizontalPrecincts = ['TL', 'TR', 'BL', 'BR'];
  // verticalPrecincts (['B', 'T']) are implicitly derived from the first-letter of the horizontal precincts.
  // Note:Only ('B') formally verified.

  /**
   * This function adds a new visual object. Upon call the item must be in the DOM, perhaps off screen
   * (at us.kDEF_TILE_POS). It returns true for success, false otherwise.
   * @param layout
   * @returns {boolean}
   */
  self.add = function (layout) {
    if (!layout || !layout.precinct || !layout.divClass || isLayoutKnown(layout.divClass)) {
      return false;
    }

    overlays.push(layout);
    self.refresh(layout.precinct);
    return true;
  };

  /**
   * This functions updates the positions of all visual objects in the overlays
   */
  self.refresh = function () {
    horizontalPrecincts.forEach(self.refreshPrecinct);
  };

  /**
   * Updates the positions of all visual objects in the passed precinct
   * @param precinct
   */
  self.refreshPrecinct = function (precinct) {
    const offsetHeight = refreshVerticalPrecinct(precinct);
    refreshHorizontalPrecinct(precinct, offsetHeight);
  };

  function refreshVerticalPrecinct (precinct) {
    let rollingHeight = 0;
    const width = dims.w;
    const height = dims.h;

    const list = getVerticalPrecinct(precinct);

    list.forEach(item => {
      const $div = $viewportDiv.find('.' + item.divClass);

      if (!$div.is(':visible')) {
        return;
      }

      const divWidth = $div.outerWidth(true);
      const divHeight = $div.outerHeight(true);
      const bottomOffset = item.bottomOffset || 0;

      // Calculate the relative position in the viewport to place the element
      rollingHeight = rollingHeight + divHeight + bottomOffset;
      const x = Math.round((width - divWidth) / 2);
      const y = (item.precinct === 'B')
        ? Math.round(height - rollingHeight)
        : 0; // TODO: Implement "T" when needed

      const position = new Point(x, y).toInt();
      $div.css({'left': position.x + 'px', 'top': position.y + 'px'});
    });
    return rollingHeight;
  }

  function refreshHorizontalPrecinct (precinct, offsetHeight = 0) {
    let rollingWidth = 0;
    const width = dims.w;
    const height = dims.h;

    const list = getSamePrecinct(precinct); // collect a flow-order list of indices into the overlays of elements in this precinct

    list.forEach((item, i) => {
      const $div = $viewportDiv.find('.' + item.divClass);

      if (!$div.is(':visible')) {
        return;
      }

      // if this element does not want to move, if it is in the view then do not reposition it
      if (item.hasOwnProperty('canBePlaced') && !item.canBePlaced) {
        const offset = $div.offset(); // is this element is fully contained in the viewport?
        if (offset.left + $div.width() > width && offset.top + $div.height() > height) {
          item.canBePlaced = true; // no, then re-enable positioning of the element
        } else {
          return; // yes, then don't reposition
        }
      }

      const divWidth = $div.outerWidth(true);
      const divHeight = $div.outerHeight(true);
      const bottomOffset = item.bottomOffset || 0;
      const rightOffset = item.rightOffset || 0;

      // Calculate the relative position in the viewport to place the element
      const x = item.precinct.endsWith(('L'))
        ? us.kOVERLAY_ELEMENT_SPACE * (i + 1) + rollingWidth + rightOffset
        : width - divWidth - rollingWidth - (i + 1) * us.kOVERLAY_ELEMENT_SPACE - rightOffset;

      const y = item.precinct.startsWith('T')
        ? us.kOVERLAY_ELEMENT_SPACE + bottomOffset
        : height - divHeight - us.kOVERLAY_ELEMENT_SPACE - bottomOffset - offsetHeight;

      const position = new Point(x, y).toInt();
      $div.css({'left': position.x + 'px', 'top': position.y + 'px'});

      // accumulate the previous elements' dimensions
      rollingWidth += divWidth;
    });
  }

  /**
   * Returns true if the divLayout is not already in the overlays.
   * @param className
   * @returns {boolean}
   */
  function isLayoutKnown (className) {
    return !!overlays.find(o => o.divClass === className);
  }

  /**
   * Returns an array of indices with horizontalPrecincts of the passed divLayout
   * @param precinct
   * @returns {Array}
   */
  function getSamePrecinct (precinct) {
    return overlays.filter(o => o.precinct === precinct).sort((a, b) => a.order - b.order);
  }

  /**
   * Returns an array of indices with verticalPrecincts of the passed divLayout
   * @param precinct
   * @returns {Array}
   */
  function getVerticalPrecinct (precinct) {
    return getSamePrecinct(precinct.charAt(0));
  }

  viewport.events.on('viewport:resize', self.refresh);
}



// WEBPACK FOOTER //
// ./src/uScope/js/overLayout.js